import { makeStyles } from "tss-react/mui"
import { tailwindPalette } from "../../../theme/tailwindPalette"
import { fetchUserLocations, GetCanteenLocationsResponse, UserWeekMenu } from "../home/weekMenuApi"
import { useEffect, useState } from "react"
import { useAppDispatch, useAppSelector } from "../../../store/storeHooks"
import { selectKitchen } from "../../nonAdminUserSelectors"
import { useApiQuery } from "../../../../core/api/useApiQuery"
import { setSelectedKitchen } from "../../nonAdminUserSlice"
import { selectLoginUser } from "../../../users/userSelectors"
import AppBar from "@mui/material/AppBar"
import { Grid, Stack } from "@mui/material"
import { StandardButton } from "../../../../shared/components/buttons/StandardButton"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faChevronDown, faLocationDot } from "@fortawesome/pro-light-svg-icons"
import { AvatarMenu } from "../../../../shared/layouts/components/AvatarMenu"
import { SelectCanteenLocationDialog } from "../home/components/SelectCanteenLocationDialog"
import { UserCreateNewButton } from "./userCreateOrderButton/UserCreateNewButton"
import { USER_REPORTS_ROUTE } from "../../../../constants/routes"
import FacilitynetLogo from "../../../../shared/layouts/assets/facilit-net-logo.svg"
import { Link, useMatches } from "react-router-dom"
import { isObjectWithProp } from "../../../../lib/lang"
import { ContextDisplay } from "../../../context/components/ContextDisplay"
import { NotificationTopBarButton } from "../../../../shared/layouts/components/NotificationTopBar/NotificationTopBarButton"

const useStyles = makeStyles()(() => ({
    appBar: {
        transition: "background-color 0.2s ease, backdrop-filter 0.2s ease",
        backgroundColor: tailwindPalette.blue[50],
        backdropFilter: "blur(10px)",
        boxShadow: "none",
    },
    scrolled: {
        backgroundColor: "rgba(255, 255, 255, 0.7)",
        backdropFilter: "blur(6px)",
    },
    container: {
        padding: "0 2rem 0 1.3rem",
    },
    logo: {
        width: "auto",
    },
    logoLink: {
        display: "flex",
        alignItems: "center",
    },
    locationsButton: {
        color: "black",
        padding: "0 !important",
        "&:hover": {
            backgroundColor: "transparent",
        },
    },
}))

const transformLocations = (data: GetCanteenLocationsResponse) => {
    return data
}

export function UserTopBar() {
    const { classes, cx } = useStyles()
    const [isScrolled, setIsScrolled] = useState(false)
    const [showCanteenLocationDialog, setShowCanteenLocationDialog] = useState(false)

    const routeMatches = useMatches()

    const selectedKitchen = useAppSelector(selectKitchen)
    const dispatch = useAppDispatch()
    const user = useAppSelector(selectLoginUser)

    const environmentRoutePath = routeMatches.find(
        (match) => isObjectWithProp(match.handle, "environmentRoot") && Boolean(match.handle.environmentRoot),
    )?.pathname

    const locations = useApiQuery(fetchUserLocations, {
        queryName: UserWeekMenu.ORGANIZATION_LOCATIONS_CANTEENS,
        dependencies: {},
        select: transformLocations,
    })

    const hasLocations = Boolean(locations.data?.length)

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 0)
        }
        window.addEventListener("scroll", handleScroll)

        return () => {
            window.removeEventListener("scroll", handleScroll)
        }
    }, [])

    useEffect(() => {
        if (!locations.data?.length) {
            return
        }

        if (user?.location?.kitchen?.id) {
            const userLocation = locations.data.find((loc) => loc.id === user.location?.id)
            const userKitchen = userLocation?.kitchens.find((kitchen) => kitchen.id === user.location?.id)

            if (userKitchen && selectedKitchen?.id !== userKitchen.id) {
                dispatch(setSelectedKitchen(userKitchen))
                return
            }
        }

        // Fall back to first location/kitchen only if no kitchen is selected
        if (!selectedKitchen) {
            dispatch(setSelectedKitchen(locations.data[0].kitchens[0]))
        }
    }, [locations.data, user?.location, selectedKitchen, dispatch])

    return (
        <AppBar
            position="sticky"
            color="inherit"
            elevation={2}
            className={cx(classes.appBar, { [classes.scrolled]: isScrolled })}
        >
            <Grid className={classes.container}>
                <Grid container wrap="nowrap" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" spacing="3.3rem">
                        <Link className={classes.logoLink} to={USER_REPORTS_ROUTE}>
                            <img className={classes.logo} src={FacilitynetLogo} alt="Facilitynet" />
                        </Link>
                        {hasLocations && !locations.error && (
                            <StandardButton
                                startIcon={<FontAwesomeIcon color="black" icon={faLocationDot} />}
                                endIcon={<FontAwesomeIcon icon={faChevronDown} />}
                                className={classes.locationsButton}
                                onClick={() => setShowCanteenLocationDialog(true)}
                                fullWidth={false}
                                variant="text"
                            >
                                {selectedKitchen?.name}
                            </StandardButton>
                        )}
                        <ContextDisplay />
                    </Stack>

                    <Grid container item wrap="nowrap" width="auto" flexGrow={0} gap="1rem" alignItems="center">
                        <UserCreateNewButton />
                        <NotificationTopBarButton />
                        <AvatarMenu urlPrefix={environmentRoutePath} />
                    </Grid>
                </Grid>
            </Grid>
            {showCanteenLocationDialog && (
                <SelectCanteenLocationDialog
                    open={showCanteenLocationDialog}
                    onSelect={(location) => {
                        dispatch(setSelectedKitchen(location))
                    }}
                    selectedLocationId={selectedKitchen?.id}
                    onClose={() => setShowCanteenLocationDialog(false)}
                />
            )}
        </AppBar>
    )
}
